import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { money2, errMsg } from "./payrollHelpers";

export default function AddSpiffModal({ employeeId, employeeName, jobOptions, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [jobId, setJobId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("A spiff name is required");
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt === 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await onSubmit({
        employeeId, category: "SPIFF", adjustmentAmount: amt, date,
        jobId: jobId || null, reason: name.trim(), notes: reason.trim(),
      });
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save spiff"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="add-spiff-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Add Spiff — {employeeName}</div>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Spiff Name <span className="text-rose-500">*</span></label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="spiff-name-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. Water Heater Spiff" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Amount ($) <span className="text-rose-500">*</span></label>
              <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="spiff-amount-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. 100" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="spiff-date-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Job / Product</label>
            <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="spiff-job-select" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] bg-white">
              <option value="">— None (general incentive) —</option>
              {(jobOptions || []).map((j) => <option key={j.jobId} value={j.jobId}>{j.jobId} — {j.customer}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Reason</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="spiff-reason-input" className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px] resize-none" placeholder="Why this spiff was earned" />
          </div>
          {amount && !Number.isNaN(parseFloat(amount)) && (
            <div className="text-[11px] text-slate-400">Total: <span className="font-semibold text-slate-600">{money2(parseFloat(amount) || 0)}</span></div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-200 text-[13px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="spiff-submit-btn" className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-semibold">{busy ? "Saving…" : "Save Spiff"}</button>
        </div>
      </form>
    </div>
  );
}
